import jsonPointer from 'json-pointer';
import isFunction from 'lodash/isFunction';
import isPlainObject from 'lodash/isPlainObject';
import warning from 'warning';
import get from '../utils/get';
import combineProps from '../utils/combineProps';
import isObjectLike from '../utils/isObjectLike';
import createRef from '../utils/createRef';
import memoize from '../utils/memoize';
import toDataPath from '../utils/toDataPath';
import mergeDeep from '../utils/mergeDeep';
import getPointer from '../utils/getPointer';

const pickString = (strArr = []) => {
  return strArr.find((str) => typeof str === 'string');
};

export default class AbstractControl {
  subscriptions = new Map();

  static defaultProps = {};

  get ownContext() {
    return {
      ...this.actions,
      ...this.properties,
    };
  }

  constructor(props) {
    this.setProps(props);
  }

  setProps(props) {
    this.props = combineProps(props, this.constructor.defaultProps);
  }

  i18n(dataPath, path, defaultMessage) {
    const message = get(this.context.i18n[dataPath], path, defaultMessage);

    if (isFunction(message)) {
      return message(this.context.values, defaultMessage);
    }

    return message;
  }

  initSubscriptions(subscriptionTypes) {
    for (const type of subscriptionTypes) {
      this.subscriptions.set(type, new Set());
    }
  }

  subscribe = (event, fn) => {
    warning(isFunction(fn), 'No function passed to subscribe');

    const eventSubscription = this.subscriptions.get(event);

    if (eventSubscription) {
      eventSubscription.add(fn);
    }
  };

  unsubscribe = (event, fn) => {
    const eventSubscription = this.subscriptions.get(event);

    if (eventSubscription) {
      eventSubscription.delete(fn);
    }
  };

  trigger = (event, ...args) => {
    const eventSubscription = [...(this.subscriptions.get(event) ?? new Set())].reverse();

    for (const subscription of eventSubscription) {
      subscription(...args);
    }
  };

  triggerAsync = async (event, ...args) => {
    const eventSubscription = [...(this.subscriptions.get(event) ?? new Set())].reverse();

    return Promise.all(
      eventSubscription.map((subscription) => Promise.resolve(subscription(...args)))
    );
  };

  withContext(context) {
    this.context = context;

    return this;
  }

  getRefFromSchema = ($ref = this.$ref) => this.context.getRefFromSchema($ref);

  computeFields = (schema) => {
    const { properties = {}, propertyOrder = [] } = schema;
    const fields = Object.keys(properties)
      .sort((a, b) => {
        const aIndex = propertyOrder.indexOf(a);
        const bIndex = propertyOrder.indexOf(b);

        return (
          (aIndex > -1 ? aIndex : propertyOrder.length + 1) -
          (bIndex > -1 ? bIndex : propertyOrder.length + 1)
        );
      })
      .reduce((o, propertyName) => {
        const $ref = jsonPointer.compile(['properties', propertyName]);
        const field = this.getRefFromSchema(createRef($ref, this.$ref));

        if (Object.keys(field).length === 0) {
          return o;
        }

        const type = field.type ?? (isObjectLike(field) ? 'object' : undefined);

        return {
          ...o,
          [$ref]: {
            ...field,
            type,
          },
        };
      }, {});

    return fields;
  };

  getPropertyDataPath = memoize((propertyName, parentRef = this.$ref) => {
    const propertyRef = createRef(propertyName);
    const compiledRef = jsonPointer.compile([
      ...jsonPointer.parse(parentRef ?? ''),
      ...jsonPointer.parse(propertyRef),
    ]);

    return toDataPath(compiledRef);
  });

  handleValidateFieldsEvent = (data, errors) => {
    const validateFieldsSubscriptions = this.subscriptions.get('validateFields') ?? new Set();

    return Promise.all(
      Array.from(validateFieldsSubscriptions.values()).map(async (subscription) => {
        const fieldMap = subscription() ?? {};

        if (isPlainObject(fieldMap)) {
          const errorMessages = await Promise.all(
            Object.entries(fieldMap).map(async ([propertyName, validateFn]) => {
              const dataPath = this.getPropertyDataPath(propertyName);
              const value = getPointer(data, dataPath);

              const errorMessage = await Promise.resolve(
                validateFn(value, this.context.formStateRef.current)
              );

              if (errorMessage) {
                return {
                  errors: {
                    ...errors,
                    [dataPath]: errorMessage,
                  },
                };
              }

              return null;
            })
          );

          return mergeDeep({}, ...errorMessages);
        }

        return null;
      })
    );
  };

  getErrorMessage = (property, defaultMessage) => {
    let $ref = this.$ref.split('/').slice(1);
    let errorMessage;
    const propertyName = $ref.slice(-1);

    for (let i = $ref.length; i >= 0; i -= 2) {
      const compiledRef = jsonPointer.compile($ref);
      const schemaMessage = getPointer(this.context.schema, `${compiledRef}/errorMessage`);
      const i18nMessage = this.context.i18n[toDataPath(compiledRef)]?.errorMessage;
      const mergedMessage = { ...schemaMessage, ...i18nMessage };

      errorMessage = pickString([
        mergedMessage?.[property]?.[propertyName],
        mergedMessage?.[property]?._,
        mergedMessage?.[property],
        mergedMessage?._,
        mergedMessage,
      ]);

      if (errorMessage) {
        break;
      }

      $ref = $ref.slice(0, -2);
    }

    return errorMessage || defaultMessage;
  };
}
